import React, { useState, useMemo, useRef } from 'react';
import { ShieldQuestion, Plus, Edit, Trash2, Search, Save, X } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { makeDirtyClose } from '../lib/useDirtyClose';
import { confirmDialog } from '../lib/confirm';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const STATUS = ['open', 'ingediend', 'goedgekeurd', 'afgewezen', 'afgehandeld'];
const STATUS_CLS = { open: 'gray', ingediend: 'blue', goedgekeurd: 'green', afgewezen: 'red', afgehandeld: 'green' };

function ClaimEditor({ claim, onClose }) {
  const { t } = useT();
  const { leveranciers, onderdelen, bonnen, inkoopOrders, createGarantieclaim, updateGarantieclaim } = useData();
  const [data, setData] = useState(() => claim || {
    leverancierId: '', onderdeelId: '', inkoopOrderId: '', bonId: '',
    omschrijving: '', status: 'open', datum: new Date().toISOString().slice(0, 10), bedrag: 0, notitie: '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  const initialSig = useRef(null);
  if (initialSig.current === null) initialSig.current = JSON.stringify(data);
  const requestClose = makeDirtyClose(() => JSON.stringify(data) !== initialSig.current, onClose, t);

  const save = async () => {
    if (!data.omschrijving.trim()) { toast.error(t('Omschrijving is verplicht', 'Omschrijving is verplicht')); return; }
    setSaving(true);
    try {
      if (claim?.id) await updateGarantieclaim(claim.id, data);
      else await createGarantieclaim(data);
      toast.success(t('Claim opgeslagen', 'Claim opgeslagen'));
      onClose();
    } catch (e) { toast.error(t('Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div className="cs-modal-overlay" onClick={requestClose}>
      <div className="cs-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="cs-modal-header">
          <h3><ShieldQuestion size={16} /> {claim?.id ? t('Garantieclaim bewerken', 'Garantieclaim bewerken') : t('Nieuwe garantieclaim', 'Nieuwe garantieclaim')}</h3>
          <button className="cs-btn ghost" onClick={requestClose}><X size={16} /></button>
        </div>
        <div className="cs-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Omschrijving')} *</label>
            <input className="cs-input" value={data.omschrijving} onChange={e => upd('omschrijving', e.target.value)} data-testid="gc-omschrijving" /></div>
          <div><label className="cs-section-title">{t('Leverancier', 'Leverancier')}</label>
            <select className="cs-input" value={data.leverancierId || ''} onChange={e => upd('leverancierId', e.target.value)} data-testid="gc-leverancier">
              <option value="">— {t('Geen', 'Geen')} —</option>
              {(leveranciers || []).map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
            </select></div>
          <div><label className="cs-section-title">{t('Onderdeel', 'Onderdeel')}</label>
            <select className="cs-input" value={data.onderdeelId || ''} onChange={e => upd('onderdeelId', e.target.value)}>
              <option value="">— {t('Geen', 'Geen')} —</option>
              {(onderdelen || []).map(o => <option key={o.id} value={o.id}>{o.code ? o.code + ' · ' : ''}{o.omschrijving || o.naam}</option>)}
            </select></div>
          <div><label className="cs-section-title">{t('Inkooporder', 'Inkooporder')}</label>
            <select className="cs-input" value={data.inkoopOrderId || ''} onChange={e => upd('inkoopOrderId', e.target.value)}>
              <option value="">— {t('Geen', 'Geen')} —</option>
              {(inkoopOrders || []).map(o => <option key={o.id} value={o.id}>{o.nummer}</option>)}
            </select></div>
          <div><label className="cs-section-title">{t('Werkbon', 'Werkbon')}</label>
            <select className="cs-input" value={data.bonId || ''} onChange={e => upd('bonId', e.target.value)}>
              <option value="">— {t('Geen', 'Geen')} —</option>
              {(bonnen || []).map(b => <option key={b.id} value={b.id}>{b.nummer}</option>)}
            </select></div>
          <div><label className="cs-section-title">{t('Status')}</label>
            <select className="cs-input" value={data.status} onChange={e => upd('status', e.target.value)} data-testid="gc-status">
              {STATUS.map(s => <option key={s} value={s}>{t(s, s)}</option>)}
            </select></div>
          <div><label className="cs-section-title">{t('Bedrag (€)', 'Bedrag (€)')}</label>
            <input type="number" step="0.01" className="cs-input" value={data.bedrag} onChange={e => upd('bedrag', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Notitie')}</label>
            <textarea className="cs-input" rows={2} value={data.notitie} onChange={e => upd('notitie', e.target.value)} /></div>
        </div>
        <div className="cs-modal-footer">
          <button className="cs-btn" onClick={requestClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={save} disabled={saving} data-testid="gc-save"><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}

export default function Garantieclaims() {
  const { t } = useT();
  const { garantieclaims, leveranciers, deleteGarantieclaim } = useData();
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);

  const levNaam = (id) => (leveranciers || []).find(l => l.id === id)?.naam;
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (garantieclaims || []).filter(c => !f || (c.omschrijving || '').toLowerCase().includes(f) || (levNaam(c.leverancierId) || '').toLowerCase().includes(f));
  }, [garantieclaims, filter, leveranciers]);

  const remove = async (c) => {
    if (!(await confirmDialog(`${t('Claim verwijderen?', 'Claim verwijderen?')}`))) return;
    try { await deleteGarantieclaim(c.id); toast.success(t('Verwijderd', 'Verwijderd')); }
    catch { toast.error(t('Mislukt')); }
  };

  const open = (garantieclaims || []).filter(c => ['open', 'ingediend'].includes(c.status)).length;

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('Garantieclaims', 'Garantieclaims')}</h2><p>{open} {t('openstaand', 'openstaand')} · {(garantieclaims || []).length} {t('totaal', 'totaal')}</p></div>
        <button className="cs-btn primary" onClick={() => setEditing({})} data-testid="btn-gc-add"><Plus size={14} /> {t('Nieuwe claim', 'Nieuwe claim')}</button>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoek claim...', 'Zoek claim...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr><th>{t('Datum')}</th><th>{t('Omschrijving')}</th><th>{t('Leverancier', 'Leverancier')}</th><th style={{ textAlign: 'right' }}>{t('Bedrag')}</th><th>{t('Status')}</th><th></th></tr></thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} onDoubleClick={() => setEditing(c)} data-testid={`gc-row-${c.id}`}>
                <td>{c.datum ? new Date(c.datum).toLocaleDateString('nl-NL') : '-'}</td>
                <td style={{ fontWeight: 600 }}>{c.omschrijving}</td>
                <td>{levNaam(c.leverancierId) || '-'}</td>
                <td style={{ textAlign: 'right' }}>{c.bedrag ? eur(c.bedrag) : '-'}</td>
                <td><span className={`cs-badge ${STATUS_CLS[c.status] || 'gray'}`}>{t(c.status, c.status)}</span></td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="cs-btn ghost" onClick={() => setEditing(c)} title={t('Bewerken')}><Edit size={12} /></button>
                  <button className="cs-btn ghost danger" onClick={() => remove(c)} data-testid={`gc-delete-${c.id}`} title={t('Verwijderen')}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="cs-empty">{t('Nog geen garantieclaims.', 'Nog geen garantieclaims.')}</div>}
      </div>

      {editing && <ClaimEditor claim={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
}
